/**
 * ListenerBinder — TF-210
 *
 * Binds step-level event listeners returned by the evaluate-step API.
 * Listeners are cleaned up on step dismiss/navigation.
 *
 * MVP Listeners:
 *   - click — CTA button click tracking
 *   - scroll — Scroll depth tracking (e.g., 50%, 75%)
 *   - timer — Time-based trigger (e.g., after 5 seconds)
 *   - page_view — Page load tracking
 */

import { useEffect, useRef } from 'react';
import { funnelLogger } from '../logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ListenerConfig {
  /** Selector for click listener target (defaults to document). */
  selector?: string;
  /** Scroll depth threshold (0-100). */
  scroll_depth?: number;
  /** Timer delay in milliseconds. */
  delay_ms?: number;
  /** Analytics pixel URL to fire. */
  pixel_url?: string;
  /** Custom event name to dispatch. */
  event_name?: string;
  [key: string]: string | number | boolean | undefined;
}

interface StepListener {
  id: string;
  eventName: 'click' | 'scroll' | 'timer' | 'page_view' | string;
  config: ListenerConfig;
}

interface ListenerBinderProps {
  listeners: StepListener[];
  /** Lifecycle mode from the funnel — controls how often listeners fire. */
  triggerLifecycle?: string;
  /** Unique step ID to track listener state. */
  stepId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getListenerKey(stepId: string, listenerId: string): string {
  return `funnel_listener_${stepId}_${listenerId}`;
}

function hasListenerFired(stepId: string, listenerId: string, lifecycle: string): boolean {
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') return false;
  const key = getListenerKey(stepId, listenerId);
  if (lifecycle === 'lifetime' || lifecycle === 'once_ever') return localStorage.getItem(key) === 'fired';
  if (lifecycle === 'session' || lifecycle === 'once_per_session') return sessionStorage.getItem(key) === 'fired';
  if (lifecycle === 'once_per_day') {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return localStorage.getItem(key) === today;
  }
  return false;
}

function markListenerFired(stepId: string, listenerId: string, lifecycle: string): void {
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') return;
  const key = getListenerKey(stepId, listenerId);
  if (lifecycle === 'lifetime' || lifecycle === 'once_ever') localStorage.setItem(key, 'fired');
  else if (lifecycle === 'session' || lifecycle === 'once_per_session') sessionStorage.setItem(key, 'fired');
  else if (lifecycle === 'once_per_day') localStorage.setItem(key, new Date().toISOString().slice(0, 10));
}

function fireCallback(config: ListenerConfig): void {
  // Fire analytics pixel if configured
  if (config.pixel_url) {
    const img = new Image();
    img.src = config.pixel_url;
  }
  // Dispatch custom event if configured
  if (config.event_name) {
    window.dispatchEvent(new CustomEvent(config.event_name, { detail: config }));
  }
}

// ---------------------------------------------------------------------------
// ListenerBinder Component
// ---------------------------------------------------------------------------

export function ListenerBinder({ listeners, triggerLifecycle = 'session', stepId }: ListenerBinderProps) {
  const cleanupRef = useRef<Array<() => void>>([]);

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    for (const listener of listeners) {
      if (hasListenerFired(stepId, listener.id, triggerLifecycle)) continue;

      switch (listener.eventName) {
        case 'click': {
          const handler = () => {
            markListenerFired(stepId, listener.id, triggerLifecycle);
            fireCallback(listener.config);
          };
          const target = listener.config.selector
            ? document.querySelector(listener.config.selector)
            : document;
          if (target) {
            target.addEventListener('click', handler, { once: true });
            cleanups.push(() => target.removeEventListener('click', handler));
          }
          break;
        }
        case 'scroll':
        case 'scroll_depth': {
          const threshold = listener.config.scroll_depth ?? 50;
          const handler = () => {
            const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (scrollableHeight <= 0) return;
            const scrollPercent = (window.scrollY / scrollableHeight) * 100;
            if (scrollPercent >= threshold) {
              markListenerFired(stepId, listener.id, triggerLifecycle);
              fireCallback(listener.config);
              window.removeEventListener('scroll', handler);
            }
          };
          window.addEventListener('scroll', handler, { passive: true });
          cleanups.push(() => window.removeEventListener('scroll', handler));
          break;
        }
        case 'timer':
        case 'timer_expired': {
          const delay = listener.config.delay_ms ?? 5000;
          const timerId = window.setTimeout(() => {
            markListenerFired(stepId, listener.id, triggerLifecycle);
            fireCallback(listener.config);
          }, delay);
          cleanups.push(() => clearTimeout(timerId));
          break;
        }
        case 'page_view': {
          // TF-AUD-014: Guard against multi-fire on re-renders.
          if (!hasListenerFired(stepId, listener.id, triggerLifecycle)) {
            markListenerFired(stepId, listener.id, triggerLifecycle);
            fireCallback(listener.config);
          }
          break;
        }
        default:
          funnelLogger.warn('Unknown listener event', {
            eventName: listener.eventName,
            listenerId: listener.id,
            stepId,
          });
      }
    }

    cleanupRef.current = cleanups;

    // Capture the array in a local variable so the cleanup closure does not
    // read through the mutable ref — prevents stale-ref listener leaks.
    const capturedCleanups = cleanups;
    return () => {
      for (const cleanup of capturedCleanups) {
        cleanup();
      }
      cleanupRef.current = [];
    };
  }, [listeners, stepId, triggerLifecycle]);

  // Renderless component
  return null;
}
